// zeroGex.js — shared 0-GEX math (SPXGamma's term for the gamma-flip / "HVL" level).
//
// One source of truth for the three 0-GEX surfaces:
//   1. ZeroGexPhase strip   — aggregate net GEX over time vs the ±band deadband
//   2. GexHeatmap overlay    — the 0-GEX line + band on the strike×time surface
//   3. price-chart study     — the 0-GEX price line + ±band corridor
//
// All functions take `points`: an ASCENDING-by-strike array of { strike, g } where
// g = net gamma at that strike (call+put cumulativeGammaNet). Callers normalize:
//   from a chainSnap:  Object.entries(byStrike).map(([k,v]) => ({ strike:+k, g:v.g }))
//   from a live chain: chain.strikes.map(k => ({ strike:k, g:strikeNetGamma(...) }))

// ── GEX Phase thresholds ──────────────────────────────────────────────────────
// SCALE CAVEAT: this band is in the units of the SUMMED cumulativeGammaNet across
// strikes (raw chain units). The intended phase bands are Positive ≥ +500 /
// Neutral ±500 / Negative ≤ −500. If live aggregate magnitudes don't sit on this
// scale, this is the ONE constant to adjust — everything downstream keys off it.
export const GEX_NEUTRAL_BAND = 500;

export const PHASE_META = {
  positive: { key: 'positive', label: 'Positive', color: '#22c55e' },
  neutral:  { key: 'neutral',  label: 'Neutral',  color: 'rgba(229, 238, 245, 0.55)' },
  negative: { key: 'negative', label: 'Negative', color: '#ef4444' },
};

// Σ net gamma across strikes — the aggregate "net GEX" the phase thresholds apply to.
export function aggregateNetGex(points) {
  let sum = 0;
  for (const p of points) sum += p.g;
  return sum;
}

// 3-state GEX Phase from the aggregate net GEX and the ±band deadband.
export function gexPhase(aggNetGex, band = GEX_NEUTRAL_BAND) {
  if (aggNetGex >= band) return 'positive';
  if (aggNetGex <= -band) return 'negative';
  return 'neutral';
}

// Price where the running cumulative net gamma (scanned low→high) crosses `target`,
// linearly interpolated between the two bracketing strikes. When `near` (spot) is
// given, returns the crossing NEAREST that price; otherwise the FIRST crossing.
// Returns null if the profile never reaches `target`.
//
// The `near` arg fixes a real artifact: a full 0DTE chain runs deep into OTM puts,
// whose net-gamma wobble can straddle `target` far below spot (~4600 SPX), so the
// first-crossing scan returns a nonsensical flip level. The flip that matters is the
// one by spot, so callers with a live spot pass it.
function cumulativeCross(points, target, near = null) {
  let running = 0;
  let prevK = null;
  let prevRun = null;
  let best = null;
  for (const { strike, g } of points) {
    const cur = running + g;
    if (prevRun !== null) {
      const a = prevRun - target;
      const b = cur - target;
      let hit = null;
      if (a === 0) hit = prevK;
      else if ((a < 0) !== (b < 0)) {            // straddles target
        const f = a / (a - b);                   // 0..1 along [prevK, strike]
        hit = prevK + f * (strike - prevK);
      }
      if (hit !== null) {
        if (near == null) return hit;            // first crossing
        if (best == null || Math.abs(hit - near) < Math.abs(best - near)) best = hit;
      }
    }
    running = cur;
    prevK = strike;
    prevRun = cur;
  }
  return best;
}

// The 0-GEX level: price where the cumulative net-gamma profile crosses zero.
// Pass live `spot` so the near-spot flip wins over deep-OTM crossings.
export function zeroGexLevel(points, near = null) {
  return cumulativeCross(points, 0, near);
}

// The ±band price corridor: prices where the cumulative profile reaches −band / +band.
// These bracket the 0-GEX level and bound the Neutral zone in price space.
export function gexBandLevels(points, band = GEX_NEUTRAL_BAND, near = null) {
  return {
    lower: cumulativeCross(points, -band, near),
    upper: cumulativeCross(points, band, near),
  };
}

// Net GEX time series from the greeks WS feed (GreeksWebsocketDataStore
// greekExposures.spx): gamma rows' net_exposure, ascending by time.
//
// THIS is the canonical "Net GEX" the 0-GEX lifecycle thresholds are calibrated
// to — MISD zero_gex reads the same stream (gex_lifecycle.extract_current_net_gex
// → greeks.latest.gamma.net_exposure) and applies ±500 (trade suitability) /
// ±1000 (expansion) to it. Unlike the chain-sum aggregate above (cumulative
// session flow, path-dependent), net_exposure is spot-responsive exposure and
// round-trips intraday. Feed timestamps can be epoch seconds or milliseconds;
// output t is epoch milliseconds.
// Deduped by timestamp (last write wins — the feed can re-emit a minute).
export function gammaNetExposureSeries(exposures) {
  if (!exposures?.length) return [];
  const byT = new Map();
  for (const e of exposures) {
    if (e?.greek_type !== 'gamma') continue;
    const ts = Number(e.timestamp);
    const v = Number(e.net_exposure);
    if (!Number.isFinite(ts) || !Number.isFinite(v)) continue;
    const t = ts < 10_000_000_000 ? ts * 1000 : ts;
    byT.set(t, v);
  }
  return [...byT.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([t, v]) => ({ t, v }));
}

export function zeroGexCrossingsFromSeries(series = [], snaps = []) {
  const firstVisible = snaps[0]?.t;
  const lastVisible = snaps[snaps.length - 1]?.t;
  const found = [];
  for (let i = series.length - 1; i > 0; i -= 1) {
    if ((series[i].v >= 0) === (series[i - 1].v >= 0)) continue;
    const a = series[i - 1];
    const b = series[i];
    const crossT = a.t + (a.v / (a.v - b.v)) * (b.t - a.t);
    if (Number.isFinite(firstVisible) && crossT < firstVisible) continue;
    if (Number.isFinite(lastVisible) && crossT > lastVisible) continue;
    let bestIdx = 0;
    for (let k = 1; k < snaps.length; k += 1) {
      if (Math.abs(snaps[k].t - crossT) < Math.abs(snaps[bestIdx].t - crossT)) {
        bestIdx = k;
      }
    }
    if (Number.isFinite(snaps[bestIdx]?.spot)) {
      found.unshift({ crossT, price: snaps[bestIdx].spot, snapIdx: bestIdx });
    }
  }
  return found;
}

// Minute-column times (snap `t`) that carry a 0-GEX crossing diamond. Pass the
// same snaps array the crossings were built from (full snaps for LC, viewSnaps
// for Pro). Used to flag a 0-GEX presence in the hovered column.
export function zeroGexCrossingTimes(crossings = [], snaps = []) {
  const out = new Set();
  for (const c of crossings) {
    const t = snaps[c.snapIdx]?.t;
    if (t != null) out.add(t);
  }
  return out;
}

// Convenience: build the normalized `points` array from a chainSnap's byStrike map.
export function pointsFromByStrike(byStrike) {
  return Object.entries(byStrike)
    .map(([k, v]) => ({ strike: Number(k), g: Number(v?.g) || 0 }))
    .sort((a, b) => a.strike - b.strike);
}

// Convenience: build `points` directly from the live optionsStore rows (rowsBySym),
// summing call+put net gamma per strike (mirrors optionsStore.captureChainSnapshot).
// Lets the price-chart study compute 0-GEX without depending on the snapshot loop.
export function pointsFromRows(rowsBySym) {
  const byK = new Map();
  for (const sym in rowsBySym) {
    const r = rowsBySym[sym];
    const k = Number(r?.strike);
    if (!Number.isFinite(k)) continue;
    if (r.optionType !== 'C' && r.optionType !== 'P') continue;
    byK.set(k, (byK.get(k) || 0) + (Number(r.cumulativeGammaNet) || 0));
  }
  return [...byK.entries()]
    .map(([strike, g]) => ({ strike, g }))
    .sort((a, b) => a.strike - b.strike);
}
